import React from 'react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge, Button, StatusBadge } from '@/components/ui';
import { PLAN_CONFIG } from '../types';
import type { Company, SubscriptionPlanCode } from '../types';
import { useNavigate } from 'react-router-dom';
import { AuthButton } from '@/core/permissions/AuthButton';

interface CompaniesDesktopTableProps {
  companies: Company[];
  onLock: (id: string) => void;
  onUnlock: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (company: Company) => void;
  onActivate: (id: string) => void;
  onViewDetail: (id: string) => void;
  onImpersonate: (id: string) => void;
  canDelete: (permission: string, action: string) => boolean;
}

export function CompaniesDesktopTable({ companies, onLock, onUnlock, onDelete, onEdit, onActivate, onViewDetail, onImpersonate, canDelete }: CompaniesDesktopTableProps) {
  const navigate = useNavigate();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tổ chức</TableHead>
          <TableHead>Gói & Rank</TableHead>
          <TableHead className="text-center">Dự án</TableHead>
          <TableHead className="text-center">Users</TableHead>
          <TableHead>Vị trí</TableHead>
          <TableHead>Trạng thái</TableHead>
          <TableHead className="text-right">Hành động</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {companies.map((company) => {
          const planCfg = PLAN_CONFIG[company.subscription_plan_code as SubscriptionPlanCode] || PLAN_CONFIG.free;
          const isLocked = company.is_locked;
          const isActive = company.is_active;

          return (
            <TableRow
              key={company.id}
              className="group hover:bg-slate-50/80 transition-all border-slate-50 cursor-pointer"
              onClick={() => onViewDetail(company.id)}
            >
              <TableCell className="py-4 pl-6">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 ${isLocked ? 'bg-red-50 text-red-400' : 'bg-slate-50 text-slate-400 group-hover:bg-white group-hover:shadow-md'
                    }`}>
                    <i className={`${isLocked ? 'ri-lock-2-fill' : 'ri-building-4-line'} text-xl`} />
                  </div>
                  <div>
                    <p className="sm:text-sm text-xs font-semibold text-slate-900 group-hover:text-brand-700 transition-colors">{company.name}</p>
                    <p className="text-[10px] text-os-muted font-mono mt-0.5 tracking-tighter">{company.code}</p>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="custom" className={`${planCfg.badge} font-black text-[10px] px-3 py-1`}>
                  {planCfg.label}
                </Badge>
              </TableCell>
              <TableCell className="text-center">
                <div className="inline-flex flex-col items-center">
                  <span className="sm:text-sm text-xs font-semibold text-slate-900">{company.current_projects || 0}</span>
                  <span className="text-[9px] font-bold text-os-muted ">Projects</span>
                </div>
              </TableCell>
              <TableCell className="text-center">
                <div className="inline-flex flex-col items-center">
                  <span className="sm:text-sm text-xs font-semibold text-slate-900">{company.current_users || 0}</span>
                  <span className="text-[9px] font-bold text-os-muted ">Seats</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5 text-slate-700 font-medium sm:text-sm text-xs">
                  <i className="ri-map-pin-2-line text-slate-300" />
                  {company.city || 'Chưa cập nhật'}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <StatusBadge
                    status={isLocked ? 'Bị khóa' : !isActive ? 'Chờ duyệt' : 'Hoạt động'}
                  />
                </div>
              </TableCell>
              <TableCell className="pr-6">
                <div className="flex items-center justify-end gap-1 transition-opacity">
                  {!isActive && !isLocked && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/20 px-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        onActivate(company.id);
                      }}
                    >
                      <i className="ri-checkbox-circle-line mr-1.5" />
                      Duyệt
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-brand-600 hover:bg-brand-50"
                    onClick={(e) => { e.stopPropagation(); onViewDetail(company.id); }}
                  >
                    <i className="ri-eye-line" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={isLocked ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:text-red-600 hover:bg-red-50"}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isLocked) onUnlock(company.id);
                      else onLock(company.id);
                    }}
                  >
                    <i className={isLocked ? "ri-lock-unlock-line" : "ri-lock-2-line"} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-purple-600 hover:bg-purple-50"
                    title="Đăng nhập ẩn danh (Impersonate)"
                    onClick={(e) => { e.stopPropagation(); onImpersonate(company.id); }}
                  >
                    <i className="ri-spy-fill" />
                  </Button>
                  <AuthButton
                    module="companies"
                    action="edit"
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                    onClick={(e) => { e.stopPropagation(); navigate(`/companies/edit/${company.id}`); }}
                  >
                    <i className="ri-edit-line" />
                  </AuthButton>
                  <AuthButton
                    module="companies"
                    action="delete"
                    variant="ghost"
                    size="sm"
                    className="text-slate-300 hover:text-red-600 hover:bg-red-50"
                    disabled={!canDelete('companies', 'delete')}
                    onClick={(e) => { e.stopPropagation(); onDelete(company.id); }}
                  >
                    <i className="ri-delete-bin-line" />
                  </AuthButton>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
